import { Fragment, useState, useEffect, useCallback } from 'react';
import { useFacility } from '../contexts/FacilityContext';
import { useCanMoveMoney, MONEY_ROLE_HINT } from '../contexts/AuthContext';
import {
  fetchUnappliedCash, applyUnappliedCash, refundUnappliedCash, voidUnappliedCash,
} from '../services/api';
import { Skeleton } from './Skeleton';
import './UnappliedCashPanel.css';

// ─────────────────────────────────────────────────────────────────────────────
// Unapplied patient cash (plan Step 12 — D4, S12-c, S12-d, S12-f, S12-h).
//
// Money a patient paid that the source system sent BEFORE the RCM had a claim
// to put it on — a copay at check-in, a checkout payment before the note was
// signed. It applies automatically when the encounter arrives. What is listed
// here is what has NOT applied yet: the encounter has not arrived, the source
// sent no encounter at all, or the encounter turned out to be another patient's.
//
// ONE ROW PER ALLOCATION, never per receipt (acceptance criterion 2): a receipt
// split across two visits shows only the portion still waiting.
//
// ⟳ Step 14 (S14-g) — three actions per row, one allocation each: APPLY it to a
// claim a person chooses (the receipt's own patient's, at this facility), REFUND
// it (it was never posted, so no payment is reversed), or VOID it with a reason.
// Nothing here is ever deleted — it is either a refund owed or a self-pay
// receipt, and both have to be resolved by a person.
// ─────────────────────────────────────────────────────────────────────────────

const RESPONSIBILITY_LABELS = {
  copay: 'Copay',
  coinsurance: 'Coinsurance',
  deductible: 'Deductible',
  self_pay: 'Self-pay',
  other: 'Other',
};

const METHOD_LABELS = { card: 'Credit Card', cash: 'Cash', check: 'Check', ach: 'ACH', other: 'Other' };

function fmtCurrency(n) {
  return '$' + (parseFloat(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(d) {
  if (!d) return '—';
  // Date-only and datetime strings both render as the calendar day they name,
  // not shifted by the viewer's time zone.
  const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function patientName(row) {
  if (!row.patient) return null;
  return [row.patient.last_name, row.patient.first_name].filter(Boolean).join(', ');
}

function waitingFor(row) {
  if (row.patient_mismatch) {
    return { tone: 'mismatch', text: 'Encounter belongs to another patient' };
  }
  if (!row.source_encounter_id) {
    return { tone: 'none', text: 'No encounter sent' };
  }
  return { tone: 'waiting', text: 'Waiting for the encounter' };
}

export default function UnappliedCashPanel() {
  const { selectedFacility } = useFacility();
  const facilityId = selectedFacility?.facility_id ?? selectedFacility?.id ?? null;

  const canMove = useCanMoveMoney();
  const [rows, setRows] = useState([]);
  const [staleAfterDays, setStaleAfterDays] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // ⟳ Step 14 — the one row being acted on: { allocationId, kind: 'apply'|'refund'|'void' }.
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({ claimId: '', reference: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null);

  const openAction = (allocationId, kind) => {
    setAction({ allocationId, kind });
    setForm({ claimId: '', reference: '', reason: '' });
    setActionError(null);
  };

  const submitAction = async (e) => {
    e.preventDefault();
    if (!action) return;
    const { allocationId, kind } = action;
    if (kind === 'apply' && !/^\d+$/.test(form.claimId.trim())) { setActionError('Enter the claim ID to apply this to.'); return; }
    if (kind === 'refund' && !form.reference.trim()) { setActionError('Enter the check number or EFT trace.'); return; }
    if (kind === 'void' && !form.reason.trim()) { setActionError('A reason is required to void.'); return; }
    setSaving(true);
    setActionError(null);
    try {
      if (kind === 'apply') await applyUnappliedCash(allocationId, Number(form.claimId.trim()));
      if (kind === 'refund') await refundUnappliedCash(allocationId, { reference: form.reference.trim(), reason: form.reason.trim() || undefined });
      if (kind === 'void') await voidUnappliedCash(allocationId, form.reason.trim());
      setAction(null);
      setNotice({ apply: 'Applied to the claim.', refund: 'Refund recorded.', void: 'Voided.' }[kind]);
      await load();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'That did not go through. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  const load = useCallback(async () => {
    if (!facilityId) { setRows([]); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchUnappliedCash(facilityId);
      setRows(res.data || []);
      setStaleAfterDays(res.stale_after_days ?? null);
    } catch (err) {
      setRows([]);
      setError(err?.response?.data?.error || 'Failed to load unapplied cash.');
    } finally {
      setLoading(false);
    }
  }, [facilityId]);

  useEffect(() => { load(); }, [load]);

  if (!facilityId) {
    return (
      <div className="payments-empty">
        <p className="payments-empty-title">Pick a facility</p>
        <p className="payments-empty-sub">Unapplied cash is listed per facility. Choose one from the Facilities list.</p>
      </div>
    );
  }

  const total = rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const staleCount = rows.filter((row) => row.stale).length;

  return (
    <div className="ucp">
      <div className="ucp-summary">
        <div>
          <span className="ucp-summary-total">{fmtCurrency(total)}</span>
          <span className="ucp-summary-label">
            {' '}unapplied across {rows.length} {rows.length === 1 ? 'portion' : 'portions'}
          </span>
        </div>
        {staleCount > 0 && (
          <span className="ucp-summary-stale">
            {staleCount} older than {staleAfterDays} days
          </span>
        )}
      </div>
      <p className="ucp-note">
        Payments the source sent before the RCM had a claim to put them on. Each one applies itself when its
        encounter arrives; one that cannot can be applied, refunded or voided here. Nothing on this list is ever deleted.
      </p>
      {notice && <div className="ucp-notice" role="status">{notice}</div>}

      {error && (
        <div className="payments-error">
          {error}
          <button className="payments-error-retry" onClick={load}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="ucp-skeleton" aria-busy="true">
          {[0, 1, 2].map((i) => <Skeleton key={i} height={44} radius={8} />)}
        </div>
      ) : rows.length === 0 && !error ? (
        <div className="payments-empty">
          <p className="payments-empty-title">No unapplied cash</p>
          <p className="payments-empty-sub">Every payment the source sent for this facility is on a claim.</p>
        </div>
      ) : rows.length > 0 && (
        <div className="payments-table-wrap">
          <table className="payments-table ucp-table">
            <thead>
              <tr>
                <th>Received</th>
                <th>Age</th>
                <th className="num">Amount</th>
                <th>Type</th>
                <th>Patient</th>
                <th>Encounter</th>
                <th>Status</th>
                <th>Receipt</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const status = waitingFor(row);
                const name = patientName(row);
                const acting = action?.allocationId === row.allocation_id ? action.kind : null;
                return (
                  <Fragment key={row.allocation_id}>
                  <tr className={row.stale ? 'ucp-row-stale' : ''}>
                    <td>{fmtDate(row.received_at)}</td>
                    <td>
                      <span className={`ucp-age ${row.stale ? 'is-stale' : ''}`}>
                        {row.age_days} {row.age_days === 1 ? 'day' : 'days'}
                      </span>
                      {row.stale && <span className="ucp-chip ucp-chip-stale">Stale</span>}
                    </td>
                    <td className="payments-td-amount">{fmtCurrency(row.amount)}</td>
                    <td>{RESPONSIBILITY_LABELS[row.responsibility_type] ?? row.responsibility_type}</td>
                    <td>
                      {name
                        ? <span className="ucp-patient">{name}</span>
                        : <span className="ucp-muted">Not yet linked ({row.source_patient_id})</span>}
                    </td>
                    <td className="ucp-mono">
                      {row.source_encounter_id ?? <span className="ucp-muted">none sent</span>}
                    </td>
                    <td><span className={`ucp-chip ucp-chip-${status.tone}`}>{status.text}</span></td>
                    <td className="ucp-receipt">
                      <span className="ucp-mono">{row.source_payment_id}</span>
                      <span className="ucp-muted">
                        {fmtCurrency(row.receipt?.amount)} · {METHOD_LABELS[row.payment_method] ?? row.payment_method}
                        {row.receipt?.status === 'partially_applied' ? ' · partly applied' : ''}
                      </span>
                    </td>
                    <td className="ucp-actions">
                      <button type="button" className="payments-action-btn" onClick={() => openAction(row.allocation_id, 'apply')} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Apply</button>
                      <button type="button" className="payments-action-btn" onClick={() => openAction(row.allocation_id, 'refund')} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Refund</button>
                      <button type="button" className="payments-action-btn" onClick={() => openAction(row.allocation_id, 'void')} disabled={!canMove} title={canMove ? undefined : MONEY_ROLE_HINT}>Void</button>
                    </td>
                  </tr>
                  {acting && (
                    <tr className="ucp-action-row">
                      <td colSpan={9}>
                        <form className="ucp-action-form" onSubmit={submitAction} aria-label={`${acting} allocation ${row.allocation_id}`}>
                          {acting === 'apply' && (
                            <label>
                              Claim ID{name ? ` (${name}'s claim at this facility)` : ''}
                              <input value={form.claimId} inputMode="numeric" onChange={(e) => setForm({ ...form, claimId: e.target.value })} />
                            </label>
                          )}
                          {acting === 'refund' && (
                            <label>
                              Check # / EFT trace
                              <input value={form.reference} maxLength={100} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
                            </label>
                          )}
                          {(acting === 'refund' || acting === 'void') && (
                            <label>
                              {acting === 'void' ? 'Reason (required)' : 'Reason'}
                              <input value={form.reason} maxLength={500} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                            </label>
                          )}
                          <button type="submit" className="payments-btn-record" disabled={saving}>
                            {saving ? 'Saving…' : { apply: 'Apply payment', refund: 'Record refund', void: 'Void' }[acting]}
                          </button>
                          <button type="button" className="payments-action-btn" onClick={() => setAction(null)} disabled={saving}>Cancel</button>
                          {actionError && <span className="ucp-action-error" role="alert">{actionError}</span>}
                        </form>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
